import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { LayoutElementV2 } from '@/components/inspector-fields/LayoutElement/LayoutElementV2';
import { InspectorHeader } from '../InspectorHeader/InspectorHeader';
import IconMoreHorizontalSquare from '@/assets/icons/icon-more-horizontal-square.svg?react';
import IconReload from '@/assets/icons/icon-reload.svg?react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Event } from '@/components/dropdowns/Event/Event';
import NodeAnimationSettings from '@/components/inspector-fields/AnimationSettings/NodeAnimationSettings';
import NodeStrokeSettings from '@/components/inspector-fields/SliderField/NodeStrokeSettings';
import { useNode } from '@craftjs/core';
import type { ProcessedFont } from '@builder4/shared/types/fonts';
import { useFonts } from '@/context/FontProvider';
import NodeCountdownSizeSettings from '@/components/inspector-fields/SliderField/NodeCountdownSizeSettings';
import NodeCountdownSpaceSettings from '@/components/inspector-fields/SliderField/NodeCountdownSpaceSettings';
import { NodeGapSettingsV2 } from '@/components/inspector-fields/PaddingMarginFields/NodeGapSettingsV2';
import NodeShadowField from '@/components/inspector-fields/ShadowField/NodeShadowField';
import NodeCountdownFontFamily from '@/components/dropdowns/SelectField/NodeCountdownFontFamily';
import NodeColorField from '@/components/inspector-fields/ColorField/NodeColorField';
import NodeCountdownFontWeightSettings from '@/components/dropdowns/SelectField/NodeCountdownFontWeightSettings';
import { CountdownModes } from '@/components/inspector-fields/CountdownModes/CountdownModes';
import { SegmentedControl } from '@/components/inspector-fields/SegmentedControl';
import { DatePicker } from '@/components/inspector-fields/DatePicker';
import { TimePicker } from '@/components/inspector-fields/TimePicker';
import { DurationPicker } from '@/components/inspector-fields/DurationPicker';
import { Redirect } from '@/components/dropdowns/Redirect/Redirect';
import type { CountdownProps, CountdownMode } from '@builder4/shared/components/Countdown';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useAppDispatch } from '@/store/hooks';
import { validatePastDate, validateUnitVisibility } from '@/utils/countdownValidation';
import { showToast } from '@/store/notifications/slice';

interface InspectorCountdownProps {
  className: string;
  text: string;
}

// Reusable options constants
const VISIBILITY_OPTIONS = [
  { label: 'Show', value: 'show' as const },
  { label: 'Hide', value: 'hide' as const },
];

const TYPE_OPTIONS = [
  { label: 'Default', value: 'default' as const },
  { label: 'Short', value: 'short' as const },
];

// Helper function to convert boolean to 'show'/'hide'
const getVisibilityValue = (isVisible: boolean): 'show' | 'hide' => {
  return isVisible ? 'show' : 'hide';
};

export const InspectorCountdown: React.FC<InspectorCountdownProps> = ({
  className,
  text = 'Countdown',
}) => {
  const dispatch = useAppDispatch();

  const {
    actions: { setProp },
    props,
    displayName,
  } = useNode((node) => ({
    props: node.data.props as CountdownProps,
    displayName: node.data.displayName,
  }));

  const { setBPProp, getBPProp } = useBreakpointAwareProp(displayName);

  const mode = getBPProp(props, 'mode');
  const type = getBPProp(props, 'type');
  const showDays = getBPProp(props, 'showDays');
  const showHours = getBPProp(props, 'showHours');
  const showMinutes = getBPProp(props, 'showMinutes');
  const showSeconds = getBPProp(props, 'showSeconds');

  const dateTime = getBPProp(props, 'dateTime');
  const evergreenDuration = getBPProp(props, 'evergreenDuration');

  const { fonts } = useFonts();

  const [selectedNumberFontFamily, setSelectedNumberFontFamily] = useState<ProcessedFont>();
  const [selectedLabelFontFamily, setSelectedLabelFontFamily] = useState<ProcessedFont>();

  useEffect(() => {
    if (props.numberFontFamily !== undefined && fonts.length > 0) {
      const selected = fonts.find((font) => font.family === props.numberFontFamily);
      setSelectedNumberFontFamily(selected);
    }
  }, [props.numberFontFamily, fonts]);

  useEffect(() => {
    if (props.labelFontFamily !== undefined && fonts.length > 0) {
      const selected = fonts.find((font) => font.family === props.labelFontFamily);
      setSelectedLabelFontFamily(selected);
    }
  }, [props.labelFontFamily, fonts]);

  const onHandleChangeNumberFamily = (family: string) => {
    const selected = fonts.find((font) => font.family === family);
    setSelectedNumberFontFamily(selected);
  };

  const onHandleChangeLabelFamily = (family: string) => {
    const selected = fonts.find((font) => font.family === family);
    setSelectedLabelFontFamily(selected);
  };

  const handleOnChangeMode = (newMode: CountdownMode) => {
    setProp((props: CountdownProps) => {
      const currentMode = getBPProp(props, 'mode');
      const currentDateTime = getBPProp(props, 'dateTime');

      let convertedDateTime = currentDateTime;

      if (currentMode === 'standard' && newMode === 'daily') {
        // Standard → Daily: Convert UTC ISO string to local time string
        if (currentDateTime) {
          const date = new Date(currentDateTime);
          const year = date.getFullYear();
          const month = (date.getMonth() + 1).toString().padStart(2, '0');
          const day = date.getDate().toString().padStart(2, '0');
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          const seconds = date.getSeconds().toString().padStart(2, '0');
          convertedDateTime = `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
        }
      } else if (currentMode === 'daily' && newMode === 'standard') {
        // Daily → Standard: Convert local time string to UTC ISO string
        if (currentDateTime) {
          const localDate = new Date(currentDateTime);
          convertedDateTime = localDate.toISOString();
        }
      }

      setBPProp(props, 'mode', newMode);
      setBPProp(props, 'dateTime', convertedDateTime);
    }, 500);
  };

  const handleOnChangeType = (value: 'default' | 'short') => {
    setProp((props: CountdownProps) => {
      setBPProp(props, 'type', value);
    }, 500);
  };

  const handleOnChangeDaysVisibility = (value: 'show' | 'hide') => {
    const shouldBlock = validateUnitVisibility(
      'days',
      showDays,
      showHours,
      showMinutes,
      showSeconds,
      value,
      dispatch,
    );

    if (shouldBlock) return;

    setProp((props: CountdownProps) => {
      setBPProp(props, 'showDays', value === 'show');
    }, 500);
  };

  const handleOnChangeHoursVisibility = (value: 'show' | 'hide') => {
    const shouldBlock = validateUnitVisibility(
      'hours',
      showDays,
      showHours,
      showMinutes,
      showSeconds,
      value,
      dispatch,
    );

    if (shouldBlock) return;

    setProp((props: CountdownProps) => {
      setBPProp(props, 'showHours', value === 'show');
    }, 500);
  };

  const handleOnChangeMinutesVisibility = (value: 'show' | 'hide') => {
    const shouldBlock = validateUnitVisibility(
      'minutes',
      showDays,
      showHours,
      showMinutes,
      showSeconds,
      value,
      dispatch,
    );

    if (shouldBlock) return;

    setProp((props: CountdownProps) => {
      setBPProp(props, 'showMinutes', value === 'show');
    }, 500);
  };

  const handleOnChangeSecondsVisibility = (value: 'show' | 'hide') => {
    const shouldBlock = validateUnitVisibility(
      'seconds',
      showDays,
      showHours,
      showMinutes,
      showSeconds,
      value,
      dispatch,
    );

    if (shouldBlock) return;

    setProp((props: CountdownProps) => {
      setBPProp(props, 'showSeconds', value === 'show');
    }, 500);
  };

  const handleOnChangeDate = (date: string) => {
    // Preserve existing time or default to 12:00 AM (midnight)
    let hours = 0;
    let minutes = 0;

    if (dateTime) {
      // dateTime is stored as UTC ISO string, convert to local to extract time
      const existingDate = new Date(dateTime);
      if (!isNaN(existingDate.getTime())) {
        hours = existingDate.getHours();
        minutes = existingDate.getMinutes();
      }
    }

    // Standard mode: Always store as UTC ISO string (timezone-aware)
    const localDate = new Date(
      `${date}T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`,
    );
    const newDateTime = localDate.toISOString();

    setProp((props: CountdownProps) => {
      setBPProp(props, 'dateTime', newDateTime);
    }, 500);

    // Validate if the date is in the past
    validatePastDate(newDateTime, mode, dispatch);
  };

  const handleOnChangeTime = (time: string) => {
    // Parse the time string (format: "HH:MM AM/PM")
    const [timePart, period] = time.split(' ');
    const [hoursStr, minutesStr] = timePart.split(':');
    let hours = parseInt(hoursStr, 10);
    const minutes = parseInt(minutesStr, 10);

    // Convert to 24-hour format if period exists
    if (period) {
      if (period === 'PM' && hours !== 12) {
        hours += 12;
      } else if (period === 'AM' && hours === 12) {
        hours = 0;
      }
    }

    let newDateTime: string;

    if (mode === 'standard') {
      // For Standard mode: Store as UTC ISO string
      // Get the date part from existing dateTime (convert from UTC to local first)
      const existingDate = dateTime ? new Date(dateTime) : new Date();
      const year = existingDate.getFullYear();
      const month = existingDate.getMonth();
      const day = existingDate.getDate();

      // Create new date with updated time in local timezone, then convert to UTC
      const localDate = new Date(year, month, day, hours, minutes, 0);
      newDateTime = localDate.toISOString();
    } else {
      // For Daily mode: Store as local time (no timezone)
      const datePart = dateTime ? dateTime.split('T')[0] : new Date().toISOString().split('T')[0];
      const timeString = `T${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
      newDateTime = `${datePart}${timeString}`;
    }

    setProp((props: CountdownProps) => {
      setBPProp(props, 'dateTime', newDateTime);
    }, 500);

    // Validate if the date/time is in the past
    validatePastDate(newDateTime, mode, dispatch);
  };

  const handleOnChangeDuration = (durationSeconds: number) => {
    // Validate duration (checks if it's between 1 second and 365 days)
    const MAX_SECONDS = 365 * 86400;

    if (durationSeconds < 1) {
      dispatch(
        showToast({
          text: 'Duration must be at least 1 second.',
          state: 'error',
          icon: 'error',
        }),
      );
      return;
    }

    if (durationSeconds > MAX_SECONDS) {
      dispatch(
        showToast({
          text: 'Total duration cannot exceed 365 days.',
          state: 'error',
          icon: 'error',
        }),
      );
      return;
    }

    setProp((props: CountdownProps) => {
      setBPProp(props, 'evergreenDuration', durationSeconds);
    }, 500);
  };

  const handleResetForAllVisitors = () => {
    // Generate a new evergreen ID to invalidate all visitor storage
    const newEvergreenId = `eg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    setProp((props: CountdownProps) => {
      setBPProp(props, 'evergreenId', newEvergreenId);
    }, 500);

    // Show success toast
    dispatch(
      showToast({
        text: 'Countdown reset for all visitors after publish',
        state: 'info',
      }),
    );
  };

  const getDatePickerValue = () => {
    if (!dateTime) return undefined;
    // For Standard mode, dateTime is UTC ISO string - convert to local date
    const date = new Date(dateTime);
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTimePickerValue = () => {
    if (!dateTime) return '12:00 AM';

    if (mode === 'standard') {
      // For Standard mode, convert UTC to local time
      const date = new Date(dateTime);
      let hours = date.getHours();
      const minutes = date.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
    } else {
      // For Daily mode, parse local time directly
      const timePart = dateTime.split('T')[1];
      if (!timePart) return '12:00 AM';
      const [hoursStr, minutesStr] = timePart.split(':');
      let hours = parseInt(hoursStr, 10);
      const minutes = parseInt(minutesStr, 10);
      const period = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
  };

  return (
    <div
      className={clsx(
        'inspector bg-neutral-0 relative z-10 inline-flex h-[calc(100vh-52px)] max-w-[240px] flex-col border-l border-neutral-100',
        className,
      )}
    >
      <InspectorHeader
        className="max-w-[240px] text-neutral-900"
        icon={IconMoreHorizontalSquare}
        text={text}
      />

      <ScrollArea className="h-[calc(100vh-104px)] overflow-hidden">
        <div className="flex flex-col gap-3 p-3">
          <LayoutElementV2
            className=""
            onClick={() => {}}
            state="default"
            data-qa-selector="inspector-countdown-layout-element"
          />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Countdown</p>

          <CountdownModes
            modes={mode}
            onClick={handleOnChangeMode}
            data-qa-selector="inspector-countdown-modes"
          />

          {mode === 'standard' && (
            <>
              <DatePicker
                value={getDatePickerValue()}
                onChange={handleOnChangeDate}
                text="Date"
                data-qa-selector="inspector-countdown-date-picker"
              />
            </>
          )}

          {(mode === 'standard' || mode === 'daily') && (
            <>
              <TimePicker
                value={getTimePickerValue()}
                onChange={handleOnChangeTime}
                text={'Time'}
                data-qa-selector="inspector-countdown-time-picker"
              />
            </>
          )}

          {mode === 'evergreen' && (
            <>
              <DurationPicker
                value={evergreenDuration || 604800}
                onChange={handleOnChangeDuration}
                text="Duration"
                data-qa-selector="inspector-countdown-duration-picker"
              />
            </>
          )}

          <SegmentedControl
            text="Type"
            onChange={handleOnChangeType}
            value={type}
            options={TYPE_OPTIONS}
            data-qa-selector="inspector-countdown-type"
          />

          <SegmentedControl
            text="Days"
            onChange={handleOnChangeDaysVisibility}
            value={getVisibilityValue(showDays)}
            options={VISIBILITY_OPTIONS}
            data-qa-selector="inspector-countdown-days-visibility"
          />

          <SegmentedControl
            text="Hours"
            onChange={handleOnChangeHoursVisibility}
            value={getVisibilityValue(showHours)}
            options={VISIBILITY_OPTIONS}
            data-qa-selector="inspector-countdown-hours-visibility"
          />

          <SegmentedControl
            text="Mins"
            onChange={handleOnChangeMinutesVisibility}
            value={getVisibilityValue(showMinutes)}
            options={VISIBILITY_OPTIONS}
            data-qa-selector="inspector-countdown-mins-visibility"
          />

          <SegmentedControl
            text="Secs"
            onChange={handleOnChangeSecondsVisibility}
            value={getVisibilityValue(showSeconds)}
            options={VISIBILITY_OPTIONS}
            data-qa-selector="inspector-countdown-secs-visibility"
          />

          <Redirect className="" text="Redirect" data-qa-selector="inspector-countdown-redirect" />

          {mode === 'evergreen' && (
            <button
              type="button"
              onClick={handleResetForAllVisitors}
              className="bg-neutral-0 flex w-full items-center justify-center gap-2 rounded-lg border border-neutral-200 px-4 py-2.5 text-sm font-normal text-neutral-700 transition-colors hover:bg-neutral-50 active:bg-neutral-100"
              data-qa-selector="inspector-countdown-reset-button"
            >
              <IconReload className="h-5 w-5 text-neutral-500" />
              <span>Reset for all visitors</span>
            </button>
          )}

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Number</p>
          {/* Font Family */}
          <NodeCountdownFontFamily
            onSelect={onHandleChangeNumberFamily}
            type="number"
            data-qa-selector="inspector-countdown-number-font-family"
          />
          {/* Font Weight */}
          <NodeCountdownFontWeightSettings
            fontFamily={selectedNumberFontFamily}
            type="number"
            data-qa-selector="inspector-countdown-number-font-weight"
          />

          <NodeColorField
            propertyName="numberTextColor"
            solidPropertyName="numberTextSolid"
            gradientPropertyName="numberTextGradient"
            gradientEnabled
            label="Text Color"
            data-qa-selector="inspector-countdown-number-text-color"
          />

          {/* Size */}
          <NodeCountdownSizeSettings
            text="Size"
            type="number"
            data-qa-selector="inspector-countdown-number-size"
          />
          {/*Spacing */}
          <NodeCountdownSpaceSettings
            text="Spacing"
            type="number"
            data-qa-selector="inspector-countdown-number-spacing"
          />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Label</p>
          {/* Font Family */}
          <NodeCountdownFontFamily
            onSelect={onHandleChangeLabelFamily}
            type="label"
            data-qa-selector="inspector-countdown-label-font-family"
          />
          {/* Font Weight */}
          <NodeCountdownFontWeightSettings
            fontFamily={selectedLabelFontFamily}
            type="label"
            data-qa-selector="inspector-countdown-label-font-weight"
          />

          <NodeColorField
            propertyName="labelTextColor"
            solidPropertyName="labelTextSolid"
            gradientPropertyName="labelTextGradient"
            gradientEnabled
            label="Text Color"
            data-qa-selector="inspector-countdown-label-text-color"
          />

          {/* Size */}
          <NodeCountdownSizeSettings
            text="Size"
            type="label"
            data-qa-selector="inspector-countdown-label-size"
          />
          {/*Spacing */}
          <NodeCountdownSpaceSettings
            text="Spacing"
            type="label"
            data-qa-selector="inspector-countdown-label-spacing"
          />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />

          <p className="pb-1 text-xs font-semibold text-neutral-900">Appearance</p>

          <NodeColorField
            propertyName="backgroundColor"
            solidPropertyName="bgSolid"
            gradientPropertyName="bgGradient"
            gradientEnabled
            label="Background Color"
            data-qa-selector="inspector-countdown-background"
          />

          <NodeColorField
            propertyName="borderColor"
            ensureStrokeVisibleProp="borderWidth"
            label="Stroke Color"
            data-qa-selector="inspector-countdown-stroke-color"
          />

          {/* Stroke */}
          <NodeStrokeSettings data-qa-selector="inspector-countdown-stroke" />
          {/*Gap */}
          <NodeGapSettingsV2 data-qa-selector="inspector-countdown-gap" />
          {/*Shadow */}
          <NodeShadowField
            shadowTypeProp="fieldShadowType"
            shadowSettingsProp="fieldShadowSettings"
            data-qa-selector="inspector-countdown-shadow"
          />

          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <p className="pb-1 text-xs font-semibold text-neutral-900">Interaction</p>
          <Event className="" text="Event" data-qa-selector="inspector-countdown-event" />
          <div className="my-1 h-0 self-stretch border-t border-neutral-100" />
          <NodeAnimationSettings />
        </div>
      </ScrollArea>
    </div>
  );
};
